/**
 * Inbox → entity deep-link routing — Plan 08 "Entity types to deep-link"
 * table, re-pointed at the CASE page (Phase 3).
 *
 * When the item carries its codename family (`codenameFamilyCached`),
 * enquiry / report-request / plan-item rows land on the case page with
 * the node pre-selected (`/cases/{family}?item=…`) — one workspace, the
 * chain rail for context. Without a cached family (belt-and-braces;
 * uncoded rows were eliminated) the old standalone routes are used —
 * those resolve-and-redirect into the case themselves when possible.
 *
 * Reports keep their own page. For `chat_thread`, the deep-link target
 * is the **parent entity's** surface with the chat drawer auto-opened
 * (`?openChat=client`); the parent's id is carried on `extra`. A live
 * case's row (`workbench_asks`, entity the report) lands in the case's
 * workbench, on Needs you, where its items are worked.
 *
 * Returns `null` if the row can't be routed (e.g. an `extra` payload
 * missing required fields). Callers render a non-clickable row in
 * that case rather than 404-ing.
 */

import { InboxItem } from '@/types/inbox';

export interface DeepLink {
  /** React Router path, with leading slash. */
  path: string;
  /** Display label for the "Open entity" affordance. */
  label: string;
}

function casePath(family: string, itemPrefix: 'enq' | 'rr', id: string, extraQuery = ''): string {
  return `/cases/${encodeURIComponent(family)}?item=${itemPrefix}:${id}${extraQuery}`;
}

/**
 * Walk the `extra` payload for the parent-entity hint the chat inbox
 * emit sets (`chat_message_from_client` / `chat_message_from_np`).
 * Returns null when the payload is absent or malformed.
 */
function parentFromExtra(
  item: InboxItem
): { parentType: 'enquiry' | 'report_request' | 'report'; parentId: string } | null {
  const extra = item.extra ?? {};
  const t = extra.parent_entity_type;
  const id = extra.parent_entity_id;
  if (typeof t !== 'string' || typeof id !== 'string') {
    return null;
  }
  if (t !== 'enquiry' && t !== 'report_request' && t !== 'report') {
    return null;
  }
  return { parentType: t, parentId: id };
}

/** Report items that land somewhere more specific than the report page
 * itself, or are labelled for the work waiting there. */
const REPORT_KIND_TARGETS: Record<string, { query: string; label: string }> = {
  // Land with the Documents aside tab already open — that's where
  // selection happens (the standalone select-file page retired).
  file_selection_required: { query: '?aside=documents', label: 'Select report file' },
  // The queued matches live in the Match review aside tab.
  match_review_required: { query: '?aside=match-review', label: 'Review matches' },
  // The profiles waiting for a verdict live in the Profile review aside tab.
  profile_review_required: { query: '?aside=profile-review', label: 'Review profiles' },
  // The new submission lives in the Documents tab too.
  np_resubmission: { query: '?aside=documents', label: 'Review resubmission' },
  // Armadillo's conversation in the Partners tab: what arrived, which
  // order is late with its reference, and what the research desk wrote.
  // Where Armadillo is not listed the link lands on the report, whose
  // orders card shows the same.
  registry_documents_delivered: { query: '?aside=armadillo', label: 'Review documents' },
  registry_document_overdue: { query: '?aside=armadillo', label: 'Chase the order' },
  registry_order_reply: { query: '?aside=armadillo', label: "Read Armadillo's reply" },
};

function reportLink(item: InboxItem): DeepLink {
  const target = REPORT_KIND_TARGETS[item.kind];
  return {
    path: `/reports/${item.entityId}${target?.query ?? ''}`,
    label: target?.label ?? 'Open report',
  };
}

/** Plan items live under their parent Report → ReportRequest. The
 * emit-site carries the RR id on `extra` so the deep-link skips the
 * round trip. */
function planItemLink(item: InboxItem, family: string | null): DeepLink | null {
  const rrId = (item.extra ?? {}).report_request_id;
  if (typeof rrId !== 'string') {
    return null;
  }
  return {
    path: family ? casePath(family, 'rr', rrId) : `/admin/triage/${rrId}`,
    label: 'Open case',
  };
}

/** Per Plan 08: chat-thread inbox items deep-link to the parent entity's
 * surface with the chat drawer auto-opened. The emit site populates
 * `parent_entity_type` / `parent_entity_id` on `extra`; falling back to
 * the thread's own routing is impossible — there's no chat-detail page.
 *
 * openChat=client because the Phase 07 launcher only exposes the client
 * + internal tabs; chat_message_* inbox kinds fire from the client
 * thread, so the tab defaults there. */
function chatThreadLink(item: InboxItem, family: string | null): DeepLink | null {
  const parent = parentFromExtra(item);
  if (parent === null) {
    return null;
  }
  if (parent.parentType === 'report') {
    return { path: `/reports/${parent.parentId}?openChat=client`, label: 'Open chat' };
  }
  if (family) {
    const prefix = parent.parentType === 'enquiry' ? 'enq' : 'rr';
    return {
      path: casePath(family, prefix, parent.parentId, '&openChat=client'),
      label: 'Open chat',
    };
  }
  const base =
    parent.parentType === 'enquiry'
      ? `/admin/enquiries/${parent.parentId}`
      : `/admin/triage/${parent.parentId}`;
  return { path: `${base}?openChat=client`, label: 'Open chat' };
}

/** A live case's row opens its workbench on Needs you. */
function caseAsksLink(item: InboxItem, family: string | null): DeepLink | null {
  if (!family) {
    return null;
  }
  return {
    path: `/cases/${encodeURIComponent(family)}?item=rep:${item.entityId}&view=needs_you`,
    label: 'Open the workbench',
  };
}

export function deepLinkForInboxItem(item: InboxItem): DeepLink | null {
  const family = item.codenameFamilyCached ?? null;
  if (item.kind === 'workbench_asks') {
    return caseAsksLink(item, family);
  }
  switch (item.entityType) {
    case 'enquiry':
      return {
        path: family ? casePath(family, 'enq', item.entityId) : `/admin/enquiries/${item.entityId}`,
        label: 'Open case',
      };
    case 'report_request':
      return {
        path: family ? casePath(family, 'rr', item.entityId) : `/admin/triage/${item.entityId}`,
        label: 'Open case',
      };
    case 'report':
      return reportLink(item);
    case 'research_plan_item':
      return planItemLink(item, family);
    case 'chat_thread':
      return chatThreadLink(item, family);
    default:
      return null;
  }
}
